import React, { useEffect, useRef } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import "./UpcomingSalahChangesSlideshow.css";
import { getNextPrayerTime } from "../../utils/prayerTimeUtils";
import { getJummahDisplayInfo } from "../../utils/timeUtils";
import { ScreenData } from "../../types/ScreenTypes";
// Import at the top
import { getCurrentTimeInTimezone } from "../../utils/timeUtils";
import CurrentTimeBar from "../elements/CurrentTimeBar";
import { hasUpcomingSalahChanges } from "../../utils/upcomingSalahChangesEligibility";
import { alignTimetablesToToday } from "../../utils/alignTimetablesToToday";
import { readableColour } from "../../utils/colourContrast";

interface UpcomingSalahChangesSlideshowProps {
  timetables: PrayerTimes[]; // Array of 7 days of prayer times
  customizedNames: Record<string, string>;
  duration: number;
  onComplete: () => void;
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes: PrayerTimes | null;
  currentIndex: number;
  totalItems: number;
  elapsedTime: number;
  totalDuration: number;
  // Add screenData to access background settings
  screenData: ScreenData;
  // Add these new props for previous Friday's Jummah times
  prevFridayJummah1: string | null;
  prevFridayJummah2: string | null;
  prevFridayJummah3: string | null;
  prevFridayJummah4: string | null;
  // Optional custom colors for theming
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  hideWhenNoChanges?: boolean;
}

// Store the original Date object for correct sorting
interface PrayerChange {
  prayer: string;
  date: string | null;
  time: string | null;
  sortDate?: Date; // Add this property for sorting
}

// First let's create a new interface for our grouped format
interface GroupedPrayerChanges {
  date: string;
  displayDate: string;
  changes: Array<{
    prayer: string;
    time: string;
  }>;
}

const UpcomingSalahChangesSlideshow: React.FC<
  UpcomingSalahChangesSlideshowProps
> = ({
  timetables: rawTimetables,
  customizedNames,
  duration,
  onComplete,
  prayerTimes,
  tomorrowPrayerTimes,
  // Add new timer props
  currentIndex,
  totalItems,
  elapsedTime,
  totalDuration,
  screenData, // Add this parameter
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
  // Extract colors from screenData or use props
  primaryColor,
  secondaryColor,
  accentColor,
  hideWhenNoChanges = false,
}) => {
  const hasCompletedRef = useRef(false);

  const timetables = alignTimetablesToToday(rawTimetables, new Date());

  // For upcoming changes, use a black background unless a custom background image is set
  const hasBgImage = !!screenData.salahChangesBackgroundUrl;
  const containerStyle = {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: hasBgImage ? "transparent" : "#000",
    overflow: "hidden",
    fontFamily: "Arial, sans-serif",
  };

  const backgroundStyle = {};
  const hookOverlayStyle = undefined;
  const contentStyle = {
    position: "relative" as const,
    zIndex: 10,
    color: "#fff",
  };

  // Auto-advance after specified duration
  useEffect(() => {
    hasCompletedRef.current = false;
    const timer = setTimeout(() => {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onComplete();
      }
    }, duration * 1000);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  // Progress calculation for timer
  const progress = Math.min((elapsedTime / duration) * 100, 100);

  // Find upcoming changes for each prayer
  const getUpcomingChanges = (): PrayerChange[] | GroupedPrayerChanges[] => {
    // Helper function to check if a date is a Friday
    const isFriday = (date: Date): boolean => date.getDay() === 5;

    // Azure Best Practice: Map prayer keys to their corresponding customizedNames property
    const prayers = [
      { key: "JamFajr", nameKey: "fajr" },
      { key: "JamDhuhr", nameKey: "dhuhr" },
      { key: "JamAsar", nameKey: "asar" },
      { key: "JamEsha", nameKey: "isha" },
      { key: "Jummah1", nameKey: "jummah" },
      { key: "Jummah2", nameKey: "jummah" },
      { key: "Jummah3", nameKey: "jummah" },
      { key: "Jummah4", nameKey: "jummah" },
    ];

    const today = new Date();
    const changes: PrayerChange[] = [];

    prayers.forEach(({ key, nameKey }) => {
      if (!timetables || timetables.length < 1) return;

      let referenceTime: string | undefined;
      let referenceIndex = 0;

      // Special handling for JamDhuhr: ignore Fridays for both reference and change detection
      if (key === "JamDhuhr") {
        // Find the first non-Friday to use as reference
        let lastNonFridayTime: string | undefined;
        let lastNonFridayIndex = -1;
        for (let i = 0; i < timetables.length; i++) {
          const checkDate = new Date(today);
          checkDate.setDate(today.getDate() + i);
          if (!isFriday(checkDate)) {
            lastNonFridayTime = timetables[i][
              key as keyof PrayerTimes
            ] as string;
            lastNonFridayIndex = i;
            break;
          }
        }
        // Now, when searching for changes, skip Fridays
        for (let i = lastNonFridayIndex + 1; i < timetables.length; i++) {
          const changeDate = new Date(today);
          changeDate.setDate(today.getDate() + i);
          if (isFriday(changeDate)) continue; // Skip Fridays

          // If it's Saturday, and JamDhuhr matches Friday's Jummah, skip
          if (
            changeDate.getDay() === 6 && // Saturday
            timetables[i][key as keyof PrayerTimes] ===
              timetables[i - 1][key as keyof PrayerTimes] &&
            isFriday(
              new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate() + i - 1,
              ),
            )
          ) {
            continue;
          }

          const nextDayTime = timetables[i][key as keyof PrayerTimes] as string;
          if (
            !nextDayTime ||
            nextDayTime === lastNonFridayTime ||
            nextDayTime === "--:--"
          ) {
            continue;
          }
          // Found a change
          changes.push({
            prayer: customizedNames.dhuhr || "Dhuhr",
            date: formatDate(changeDate),
            time: nextDayTime,
            sortDate: changeDate,
          });
          break;
        }
        return; // Skip the rest of the normal loop for JamDhuhr
      }

      // Special handling for Jummah prayers
      if (key.startsWith("Jummah")) {
        const jummahNumber = key.replace("Jummah", "");
        const currentJummahTime = prayerTimes?.[key as keyof PrayerTimes];

        let prevFridayTime = null;
        if (key === "Jummah1") prevFridayTime = prevFridayJummah1;
        else if (key === "Jummah2") prevFridayTime = prevFridayJummah2;
        else if (key === "Jummah3") prevFridayTime = prevFridayJummah3;
        else if (key === "Jummah4") prevFridayTime = prevFridayJummah4;

        // Only show Jummah as a change if we have valid previous data AND it has actually changed
        if (
          currentJummahTime &&
          currentJummahTime !== "--:--" &&
          prevFridayTime &&
          prevFridayTime !== "--:--" &&
          prevFridayTime !== currentJummahTime
        ) {
          const prayerName =
            jummahNumber === "1"
              ? customizedNames.jummah || "Jumu'ah"
              : `${customizedNames.jummah || "Jumu'ah"} ${jummahNumber}`;

          // Get this Friday's date
          const thisFriday = new Date(today);
          const daysUntilFriday = (5 - today.getDay() + 7) % 7;
          thisFriday.setDate(today.getDate() + daysUntilFriday);

          changes.push({
            prayer: prayerName,
            date: formatDate(thisFriday),
            time: currentJummahTime as string,
            sortDate: thisFriday,
          });
        }
        return;
      }

      // For other prayers, use the first day as reference
      referenceTime = timetables[0][key as keyof PrayerTimes] as string;
      referenceIndex = 0;

      let changeFound = false;
      for (let i = referenceIndex + 1; i < timetables.length; i++) {
        const nextDayTime = timetables[i][key as keyof PrayerTimes] as string;
        const changeDate = new Date(today);
        changeDate.setDate(today.getDate() + i);

        // Skip if no change or empty value
        if (
          !nextDayTime ||
          nextDayTime === referenceTime ||
          nextDayTime === "--:--"
        ) {
          continue;
        }

        let prayerName = "";
        if (key.startsWith("Jummah")) {
          const jummahNumber = key.replace("Jummah", "");
          prayerName =
            jummahNumber === "1"
              ? customizedNames.jummah || "Jumu'ah"
              : `${customizedNames.jummah || "Jumu'ah"} ${jummahNumber}`;
        } else {
          prayerName = customizedNames[nameKey]
            ? customizedNames[nameKey]
            : nameKey.charAt(0).toUpperCase() + nameKey.slice(1);
        }

        changes.push({
          prayer: prayerName,
          date: formatDate(changeDate),
          time: nextDayTime,
          sortDate: changeDate,
        });

        changeFound = true;
        break;
      }
    });

    // After getting all changes, check how to present them
    if (screenData.salahChangesOrder === "SalahOrder") {
      // Define a specific prayer order
      const prayerOrder = {
        [customizedNames.fajr || "Fajr"]: 1,
        [customizedNames.dhuhr || "Dhuhr"]: 2,
        [customizedNames.asar || "Asar"]: 3,
        [customizedNames.maghrib || "Maghrib"]: 4,
        [customizedNames.isha || "Isha"]: 5,
        [customizedNames.jummah || "Jumu'ah"]: 6,
      };

      // Add Jummah number-specific entries
      const jummahBase = customizedNames.jummah || "Jumu'ah";
      prayerOrder[jummahBase] = 6;
      prayerOrder[`${jummahBase} 1`] = 6;
      prayerOrder[`${jummahBase} 2`] = 7;
      prayerOrder[`${jummahBase} 3`] = 8;
      prayerOrder[`${jummahBase} 4`] = 9;

      // Sort by the prayer order, then by date
      changes.sort((a, b) => {
        const aIsJummah = a.prayer.includes(jummahBase);
        const bIsJummah = b.prayer.includes(jummahBase);

        if (aIsJummah && bIsJummah) {
          const aMatch = a.prayer.match(/\d+/);
          const bMatch = b.prayer.match(/\d+/);
          const aNum = aMatch ? parseInt(aMatch[0]) : 1;
          const bNum = bMatch ? parseInt(bMatch[0]) : 1;
          if (aNum !== bNum) return aNum - bNum;
          return compareDateAndTime(a, b);
        }

        const orderA = prayerOrder[a.prayer] || 999;
        const orderB = prayerOrder[b.prayer] || 999;
        const prayerComparison = orderA - orderB;
        if (prayerComparison !== 0) return prayerComparison;
        return compareDateAndTime(a, b);
      });

      return changes;
    } else {
      // Group by date when in DateOrder mode
      changes.sort((a, b) => compareDateAndTime(a, b));
      const groupedChanges: GroupedPrayerChanges[] = [];
      const dateMap: Record<string, GroupedPrayerChanges> = {};

      changes.forEach((change) => {
        const dateKey = change.sortDate!.toDateString();
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            displayDate: change.date!,
            changes: [],
          };
          groupedChanges.push(dateMap[dateKey]);
        }
        dateMap[dateKey].changes.push({
          prayer: change.prayer,
          time: change.time!,
        });
      });

      return groupedChanges;
    }

    function compareDateAndTime(a: PrayerChange, b: PrayerChange): number {
      if (a.sortDate && b.sortDate) {
        const dateDiff = a.sortDate.getTime() - b.sortDate.getTime();
        if (dateDiff !== 0) return dateDiff;
      }
      const timeA = a.time || "00:00";
      const timeB = b.time || "00:00";
      const [hoursA, minutesA] = timeA.split(":").map(Number);
      const [hoursB, minutesB] = timeB.split(":").map(Number);
      if (hoursA !== hoursB) return hoursA - hoursB;
      return minutesA - minutesB;
    }
  };

  // Modify the formatDate function to use abbreviated day names
  const formatDate = (date: Date): string => {
    // Abbreviated day names
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dayOfWeek = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];

    // Add ordinal suffix (1st, 2nd, 3rd, 4th...)
    const getOrdinalSuffix = (d: number): string => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const isTomorrow =
      date.getDate() === tomorrow.getDate() &&
      date.getMonth() === tomorrow.getMonth() &&
      date.getFullYear() === tomorrow.getFullYear();

    // If it's tomorrow, just return "Tomorrow"
    if (isTomorrow) {
      return "Tomorrow";
    }

    const dateStr = `${dayOfWeek} ${day}${getOrdinalSuffix(day)} ${month}`;
    return dateStr;
  };

  const hasUpcomingChanges = hasUpcomingSalahChanges({
    timetables,
    prayerTimes,
    prevFridayJummah1,
    prevFridayJummah2,
    prevFridayJummah3,
    prevFridayJummah4,
  });

  useEffect(() => {
    if (hideWhenNoChanges && !hasUpcomingChanges) {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onComplete();
      }
    }
  }, [hideWhenNoChanges, hasUpcomingChanges, onComplete]);

  // Update the getJamaatTimes function
  const getJamaatTimes = (
    today: PrayerTimes,
    tomorrow: PrayerTimes | null,
    currentTimeStr: string,
    customizedNames: Record<string, string>,
  ) => {
    // Get detailed Jummah info
    const now = getCurrentTimeInTimezone(screenData.timeZone);
    const jummahInfo = getJummahDisplayInfo(
      now,
      currentTimeStr,
      today,
      screenData,
    );

    // Build the array as in your main layouts but use formatPrayerTime
    return [
      {
        label: customizedNames.fajr || "Fajr",
        time: formatPrayerTime(
          getNextPrayerTime("fajr", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        // Dhuhr/Jummah logic
        label: jummahInfo.showJummah
          ? jummahInfo.jummahNumber > 1
            ? `${customizedNames.jummah || "Jumu'ah"} ${
                jummahInfo.jummahNumber
              }`
            : customizedNames.jummah || "Jumu'ah"
          : customizedNames.dhuhr || "Dhuhr",
        time: formatPrayerTime(
          jummahInfo.showJummah
            ? jummahInfo.jummahTime || "--:--"
            : getNextPrayerTime("dhuhr", today, tomorrow, currentTimeStr)
                .iqama || "--:--",
        ),
      },
      {
        label: customizedNames.asar || "Asar",
        time: formatPrayerTime(
          getNextPrayerTime("asar", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: customizedNames.maghrib || "Maghrib",
        time: formatPrayerTime(
          getNextPrayerTime("maghrib", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: customizedNames.isha || "Isha",
        time: formatPrayerTime(
          getNextPrayerTime("isha", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
    ];
  };

  // Add this function to detect portrait mode
  const isPortraitMode = () => {
    return window.innerHeight > window.innerWidth;
  };

  // Helper functions for portrait mode (matching SimpleUpcomingSalahChangesSlideshow)
  const getFullDayName = (date: Date): string => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[date.getDay()];
  };

  const getShortDate = (date: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const isTomorrowDate = (date: Date): boolean => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return (
      date.getDate() === tomorrow.getDate() &&
      date.getMonth() === tomorrow.getMonth() &&
      date.getFullYear() === tomorrow.getFullYear()
    );
  };

  // Update the renderChangesTable function to not render empty tables
  const renderChangesTable = () => {
    const changes = getUpcomingChanges();
    const inPortraitMode = isPortraitMode();

    // Debug log to help diagnose change detection
    // console.log("Prayer changes detected:", changes.length, changes);

    if (changes.length === 0) {
      if (hideWhenNoChanges) {
        return null;
      }
      return (
        <div className="no-changes-message">
          No upcoming salaah time changes.
        </div>
      );
    }

    // Check if we're in grouped mode (DateOrder) by checking the first item structure
    const isGroupedByDate = !("prayer" in changes[0] && "date" in changes[0]);

    // In portrait mode, always use a single table
    const needsMultipleTables =
      !inPortraitMode &&
      (isGroupedByDate
        ? (changes as GroupedPrayerChanges[]).reduce(
            (total, group) => total + group.changes.length,
            0,
          ) > 2
        : changes.length > 3);

    // Portrait mode: use clean div-based layout matching SimpleUpcomingSalahChangesSlideshow
    if (inPortraitMode) {
      if (isGroupedByDate) {
        const groupedChanges = changes as GroupedPrayerChanges[];
        return (
          <div className="portrait-changes-list">
            {groupedChanges.map((dateGroup, groupIndex) => {
              const groupDate = new Date(dateGroup.date);
              const isGroupTomorrow = isTomorrowDate(groupDate);
              return (
                <div key={groupIndex} className="portrait-date-group">
                  <div
                    className={`portrait-date-header ${
                      isGroupTomorrow ? "tomorrow" : ""
                    }`}
                  >
                    <span className="day-name">
                      {isGroupTomorrow ? "Tomorrow" : getFullDayName(groupDate)}
                    </span>
                    <span className="short-date">
                      {getShortDate(groupDate)}
                    </span>
                  </div>
                  {dateGroup.changes.map((change, changeIndex) => (
                    <div
                      key={`${groupIndex}-${changeIndex}`}
                      className="portrait-change-item"
                    >
                      <span className="portrait-prayer-name">
                        {change.prayer}
                      </span>
                      <span className="portrait-time-badge">
                        {formatPrayerTime(change.time)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      } else {
        const prayerChanges = changes as PrayerChange[];
        return (
          <div className="portrait-changes-list">
            {prayerChanges.map((change, index) => {
              const changeDate = change.sortDate || new Date();
              const isChangeTomorrow = change.sortDate
                ? isTomorrowDate(change.sortDate)
                : false;
              return (
                <div
                  key={index}
                  className="portrait-change-item salah-order-item"
                >
                  <div className="portrait-prayer-info">
                    <span className="portrait-prayer-name">
                      {change.prayer}
                    </span>
                    <span
                      className={`portrait-date-info ${
                        isChangeTomorrow ? "tomorrow" : ""
                      }`}
                    >
                      {isChangeTomorrow ? "Tomorrow" : formatDate(changeDate)}
                    </span>
                  </div>
                  <span className="portrait-time-badge">
                    {formatPrayerTime(change.time)}
                  </span>
                </div>
              );
            })}
          </div>
        );
      }
    }

    // Landscape mode: use two-column layout when many changes, single column otherwise
    if (isGroupedByDate) {
      const groupedChanges = changes as GroupedPrayerChanges[];

      const renderDateGroup = (
        dateGroup: GroupedPrayerChanges,
        groupIndex: number,
      ) => {
        const groupDate = new Date(dateGroup.date);
        const isGroupTomorrow = isTomorrowDate(groupDate);
        return (
          <div key={groupIndex} className="portrait-date-group">
            <div
              className={`portrait-date-header ${
                isGroupTomorrow ? "tomorrow" : ""
              }`}
            >
              <span className="day-name">
                {isGroupTomorrow ? "Tomorrow" : getFullDayName(groupDate)}
              </span>
              <span className="short-date">{getShortDate(groupDate)}</span>
            </div>
            {dateGroup.changes.map((change, changeIndex) => (
              <div
                key={`${groupIndex}-${changeIndex}`}
                className="portrait-change-item"
              >
                <span className="portrait-prayer-name">{change.prayer}</span>
                <span className="portrait-time-badge">
                  {formatPrayerTime(change.time)}
                </span>
              </div>
            ))}
          </div>
        );
      };

      if (needsMultipleTables) {
        // Split groups into two columns
        const midpoint = Math.ceil(groupedChanges.length / 2);
        const leftGroups = groupedChanges.slice(0, midpoint);
        const rightGroups = groupedChanges.slice(midpoint);
        return (
          <div className="landscape-two-column">
            <div className="portrait-changes-list">
              {leftGroups.map((g, i) => renderDateGroup(g, i))}
            </div>
            <div className="portrait-changes-list">
              {rightGroups.map((g, i) => renderDateGroup(g, i + midpoint))}
            </div>
          </div>
        );
      }

      return (
        <div className="portrait-changes-list">
          {groupedChanges.map((dateGroup, groupIndex) =>
            renderDateGroup(dateGroup, groupIndex),
          )}
        </div>
      );
    } else {
      const prayerChanges = changes as PrayerChange[];

      const renderSalahItem = (change: PrayerChange, index: number) => {
        const changeDate = change.sortDate || new Date();
        const isChangeTomorrow = change.sortDate
          ? isTomorrowDate(change.sortDate)
          : false;
        return (
          <div key={index} className="portrait-change-item salah-order-item">
            <div className="portrait-prayer-info">
              <span className="portrait-prayer-name">{change.prayer}</span>
              <span
                className={`portrait-date-info ${
                  isChangeTomorrow ? "tomorrow" : ""
                }`}
              >
                {isChangeTomorrow ? "Tomorrow" : formatDate(changeDate)}
              </span>
            </div>
            <span className="portrait-time-badge">
              {formatPrayerTime(change.time)}
            </span>
          </div>
        );
      };

      if (needsMultipleTables) {
        const midpoint = Math.ceil(prayerChanges.length / 2);
        const leftItems = prayerChanges.slice(0, midpoint);
        const rightItems = prayerChanges.slice(midpoint);
        return (
          <div className="landscape-two-column">
            <div className="portrait-changes-list">
              {leftItems.map((c, i) => renderSalahItem(c, i))}
            </div>
            <div className="portrait-changes-list">
              {rightItems.map((c, i) => renderSalahItem(c, i + midpoint))}
            </div>
          </div>
        );
      }

      return (
        <div className="portrait-changes-list">
          {prayerChanges.map((change, index) => renderSalahItem(change, index))}
        </div>
      );
    }
  };

  // Get current time for footer
  const now = new Date();
  const currentTimeStr = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  // Update the formatTime function to remove the hair space
  const formatTime = (timeStr: string): string => {
    if (!timeStr || timeStr === "--:--") return timeStr;

    // If 24-hour format is requested, just return as is
    if (!screenData.use12HourFormat) return timeStr;

    // Convert 24h to 12h format
    const [hours, minutes] = timeStr.split(":").map(Number);
    const period = hours >= 12 ? "pm" : "am"; // Changed to lowercase
    const hour12 = hours % 12 || 12; // Convert 0 to 12

    // Return without any extra space between time and period
    return `${hour12}:${minutes.toString().padStart(2, "0")}${period}`;
  };

  // Update formatPrayerTime function to properly wrap AM/PM in span with period class
  const formatPrayerTime = (time: string | null): React.ReactNode => {
    if (!time) return "--:--";

    const formatted = formatTime(time);

    if (
      !screenData.use12HourFormat ||
      (!formatted.includes("am") && !formatted.includes("pm"))
    ) {
      return formatted;
    }

    // Find the position of "am" or "pm"
    const amIndex = formatted.indexOf("am");
    const pmIndex = formatted.indexOf("pm");
    const periodIndex = amIndex >= 0 ? amIndex : pmIndex;

    if (periodIndex >= 0) {
      const timePart = formatted.substring(0, periodIndex);
      const periodPart = formatted.substring(periodIndex, periodIndex + 2);

      return <>{timePart}</>;
    }

    return formatted;
  };

  const badgeColour = primaryColor || screenData.primaryColor || "#FFC107";
  // Create custom style variables for theming
  const themeColors = {
    "--theme-primary-color":
      primaryColor || screenData.primaryColor || "#FFC107",
    "--theme-secondary-color":
      secondaryColor || screenData.secondaryColor || "#FF9800",
    "--theme-accent-color": accentColor || screenData.accentColor || "#4CAF50",
    "--compact-primary-color":
      primaryColor || screenData.primaryColor || "#FFC107",
    "--compact-background-color": screenData.widgetBackgroundColor || "#000",
    "--heading-text-color": screenData.headingTextColor || "#FFFFFF",
    // Only overridden when the badge text would be nearly invisible on the badge
    "--badge-text-color": readableColour(
      [secondaryColor || screenData.secondaryColor || "#FF9800", screenData.headingTextColor],
      badgeColour,
      1.5,
    ),
  } as React.CSSProperties;

  // Combine container style with theme colors
  const combinedContainerStyle = {
    ...containerStyle,
    ...themeColors,
  };

  return (
    <div
      className={`upcoming-salah-changes${hasBgImage ? " upcoming-salah-changes--has-bg" : ""}`}
      style={combinedContainerStyle}
    >
      {hasBgImage && (
        <img
          src={screenData.salahChangesBackgroundUrl}
          alt=""
          className="salah-changes-bg-image"
        />
      )}
      {/* ✅ Time bar - should be on top with z-index: 100 */}
      <CurrentTimeBar
        screenData={screenData}
        prayerTimes={prayerTimes}
        tomorrowPrayerTimes={tomorrowPrayerTimes}
      />

      {/* Content area - z-index: 10 */}
      <div className="upcoming-salah-content" style={contentStyle}>
        <h1>Upcoming Salaah Time Changes</h1>
        {renderChangesTable()}
      </div>

      {/* Footer - z-index: 10 */}
      <div className="upcoming-jamaat-footer">
        <div
          className="footer-progress"
          style={{ width: `${progress}%` }}
        ></div>
        {getJamaatTimes(
          prayerTimes,
          tomorrowPrayerTimes,
          currentTimeStr,
          customizedNames,
        ).map((p) => (
          <span key={p.label} className="jamaat-time">
            <span className="jamaat-time-label">{p.label}</span>
            <strong title={p.label + " time"}>{p.time}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

export default UpcomingSalahChangesSlideshow;
